import React, { createContext, useContext, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ar from "./ar.json";
import en from "./en.json";

const dictionaries = { ar, en };
const SUPPORTED = ["ar", "en"];
const DEFAULT_LANG = "ar";

const I18nContext = createContext({
  lang: DEFAULT_LANG,
  t: (k) => k,
  changeLanguage: () => {},
});

function resolvePath(obj, path) {
  return path.split(".").reduce((acc, part) => {
    if (acc === undefined || acc === null) return undefined;
    return acc[part];
  }, obj);
}

export function I18nProvider({ lang, children }) {
  const navigate = useNavigate();
  const location = useLocation();

  const currentLang = SUPPORTED.includes(lang) ? lang : DEFAULT_LANG;

  const value = useMemo(() => {
    const dict = dictionaries[currentLang];
    const t = (key, fallback) => {
      const val = resolvePath(dict, key);
      if (val === undefined || val === null) {
        return fallback !== undefined ? fallback : key;
      }
      return val;
    };
    const changeLanguage = (target) => {
      if (!SUPPORTED.includes(target)) return;
      const parts = location.pathname.split("/").filter(Boolean);
      if (parts.length && SUPPORTED.includes(parts[0])) {
        parts[0] = target;
      } else {
        parts.unshift(target);
      }
      const nextPath = "/" + parts.join("/");
      navigate(nextPath + location.search + location.hash, {
        replace: false,
        state: { fromLanguageSwitch: true },
      });
    };
    return { lang: currentLang, t, changeLanguage };
  }, [currentLang, location.pathname, location.search, location.hash, navigate]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}

export function LanguageRoute({ children }) {
  const { lang } = useParams();
  return <I18nProvider lang={lang}>{children}</I18nProvider>;
}

export { SUPPORTED, DEFAULT_LANG };
